import { useLayoutEffect, useReducer, useRef, useState, type ReactNode } from "react";
import { Check, X, CheckCheck, Undo2, Zap, Table2, Sparkles, MapPin } from "lucide-react";
import { type Artifact, type TableArtifactData, type TableColumn, type TableRow, isArtifactResponded } from "@shared";
import { cn } from "@lib/utils";
import { Button } from "@components/ui/button";
import { initialReviewState, reduceReview, statusCounts, applyStatuses } from "./reviewState";

/** Per-cell renderer; the generic default stringifies row[column.key]. */
export type RenderCell = (column: TableColumn, row: TableRow) => ReactNode;

/** A row laid out for the Simple view — structured parts, placed by the table. */
export interface SimpleRowParts {
  tier?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  metric?: ReactNode;
}
export type RenderSimpleRow = (row: TableRow) => SimpleRowParts;

export interface ReviewableTableProps {
  artifact: Artifact;
  /** Approve handoff: receives the whole artifact with current statuses stamped. */
  onApprove: (artifact: Artifact) => void;
  /** Block all controls (e.g. while a run is streaming — no submit mid-run). */
  disabled?: boolean;
  title?: string;
  subtitle?: ReactNode;
  renderCell?: RenderCell;
  /** Provide to enable the Simple view + the Expert/Simple toggle. */
  renderSimpleRow?: RenderSimpleRow;
  /** Caption next to the toggle, e.g. "Sorted by best opportunity". */
  sortedLabel?: string;
  /** Override persisted column labels by key (frontend owns headers). */
  columnLabels?: Record<string, string>;
  /** The market the metrics are scoped to (e.g. "Canada"), shown top-right. */
  location?: string;
}

const defaultRenderCell: RenderCell = (column, row) => {
  const value = (row as Record<string, unknown>)[column.key];
  return value == null ? "" : String(value);
};

const DOT = {
  accepted: "bg-emerald-500",
  declined: "bg-red-500",
  pending: "bg-neutral-300",
} as const;

function RowActions({
  row,
  status,
  disabled,
  dispatch,
}: {
  row: TableRow;
  status: "pending" | "accepted" | "declined";
  disabled: boolean;
  dispatch: React.Dispatch<{ type: "accept" | "decline"; id: string }>;
}) {
  return (
    <div className="flex items-center justify-end gap-0.5">
      <button
        type="button"
        aria-label={`Accept ${row.id}`}
        aria-pressed={status === "accepted"}
        disabled={disabled}
        onClick={() => dispatch({ type: "accept", id: row.id })}
        className={cn(
          "grid place-items-center size-7 rounded-md border transition-colors disabled:opacity-50",
          status === "accepted"
            ? "bg-emerald-500 border-emerald-500 text-white"
            : "bg-white border-neutral-200 text-neutral-400 hover:border-emerald-400 hover:text-emerald-500"
        )}
      >
        <Check className="size-3.5" />
      </button>
      <button
        type="button"
        aria-label={`Reject ${row.id}`}
        aria-pressed={status === "declined"}
        disabled={disabled}
        onClick={() => dispatch({ type: "decline", id: row.id })}
        className={cn(
          "grid place-items-center size-7 rounded-md border transition-colors disabled:opacity-50",
          status === "declined"
            ? "bg-red-500 border-red-500 text-white"
            : "bg-white border-neutral-200 text-neutral-400 hover:border-red-400 hover:text-red-500"
        )}
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: "simple" | "expert";
  onChange: (v: "simple" | "expert") => void;
}) {
  const tab = (v: "simple" | "expert", icon: ReactNode, label: string) => (
    <button
      type="button"
      onClick={() => onChange(v)}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md px-4 py-1.5 text-xs font-medium transition-colors",
        view === v ? "bg-white border border-neutral-300 text-neutral-900 shadow-sm" : "text-neutral-500 hover:text-neutral-800"
      )}
    >
      {icon}
      {label}
    </button>
  );
  return (
    <div className="inline-flex items-center gap-0.1 rounded-md bg-neutral-100 p-1">
      {tab("expert", <Table2 className="size-3.5" />, "Expert")}
      {tab("simple", <Sparkles className="size-3.5" />, "Simple")}
    </div>
  );
}

export function ReviewableTable({
  artifact,
  onApprove,
  disabled = false,
  title = "Review",
  subtitle,
  renderCell = defaultRenderCell,
  renderSimpleRow,
  sortedLabel,
  columnLabels,
  location,
}: ReviewableTableProps) {
  const data = artifact.data as TableArtifactData;
  const [{ statuses, skippedByRest }, dispatch] = useReducer(reduceReview, data.rows, initialReviewState);
  const [view, setView] = useState<"simple" | "expert">(renderSimpleRow ? "simple" : "expert");
  const counts = statusCounts(statuses);
  const simple = view === "simple" && !!renderSimpleRow;
  const allAccepted = data.rows.length > 0 && counts.approved === data.rows.length;
  const skipActive = skippedByRest.length > 0;
  // Read-only once the user has responded — explicit state on the artifact
  // (status `completed`), set by the approve submit. Not inferred from rows or
  // surrounding chat messages. The caller can also lock it (e.g. while streaming).
  const locked = disabled || isArtifactResponded(artifact);

  // Keep a steady table-viewport height (~the same in Simple and Expert, whose
  // rows differ in height) and always leave the next row half-peeking so it
  // reads "there's more to scroll." We fit as many whole rows as the target
  // allows, then add half a row. Expert's header lives in the scroll area, so
  // it's measured and excluded from the row budget. When the data is shorter
  // than the viewport there's nothing to imply — it just shows every row.
  const SCROLL_VIEWPORT_PX = 300;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number>();
  useLayoutEffect(() => {
    const container = scrollRef.current;
    const row = container?.querySelector(simple ? "li" : "tbody tr");
    if (!(row instanceof HTMLElement)) return;
    const head = simple ? null : container?.querySelector("thead");
    const headH = head instanceof HTMLElement ? head.offsetHeight : 0;
    const rowH = row.offsetHeight;
    if (!rowH) return; // unmeasured (e.g. jsdom) — leave the area uncapped
    const fullRows = Math.max(1, Math.floor((SCROLL_VIEWPORT_PX - headH) / rowH));
    setMaxHeight(headH + (fullRows + 0.5) * rowH);
  }, [simple, data.rows.length]);

  return (
    // @container: the table lives in chat columns of very different widths
    // (full-page brainstorm vs the side chat); the layout adapts to the CARD's
    // width, not the viewport's.
    <div className="@container rounded-2xl border border-neutral-200 bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3 border-b border-neutral-100">
        <div className="flex items-start gap-3">
          <div className="grid place-items-center size-10 rounded-xl bg-base-50 text-base-500 shrink-0">
            <Table2 className="size-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-neutral-900">{title}</span>
              <span className="rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-neutral-500 bg-neutral-100">
                Reviewable Table
              </span>
            </div>
            {subtitle && <div className="text-xs text-neutral-500 mt-0.5">{subtitle}</div>}
          </div>
        </div>
        {location && (
          <div className="flex items-center gap-2 shrink-0">
            <div className="grid place-items-center size-8 rounded-lg bg-base-50 text-base-500 shrink-0">
              <MapPin className="size-4" />
            </div>
            <div className="text-right leading-tight">
              <div className="text-xs text-neutral-500">Searches per month in</div>
              <div className="text-sm font-semibold text-neutral-900">{location}</div>
            </div>
          </div>
        )}
      </div>

      {/* Toolbar: view toggle + bulk actions. Wraps instead of clipping when
          the card is narrow; the sort caption is decoration and only shows
          when the card has room for it. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 pt-3 pb-3 border-b border-neutral-100">
        <div className="flex items-center gap-3">
          {renderSimpleRow && <ViewToggle view={view} onChange={setView} />}
          {sortedLabel && (
            <span className="hidden @2xl:inline-flex items-center gap-1.5 text-xs italic text-neutral-600">
              <Sparkles className="size-3.5 text-blue-400" />
              {sortedLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Button
            variant="outline"
            size="xs"
            disabled={locked}
            onClick={() => dispatch({ type: allAccepted ? "clearAll" : "approveAll" })}
          >
            {allAccepted ? (
              <>
                <Undo2 className="size-4" /> Remove all
              </>
            ) : (
              <>
                <CheckCheck className="size-4" /> {simple ? "Add all" : "Approve all"}
              </>
            )}
          </Button>
          <Button variant="outline" size="xs" disabled={locked} onClick={() => dispatch({ type: "rejectRest" })}>
            {skipActive ? (
              <>
                <Undo2 className="size-4" /> Undo skip
              </>
            ) : (
              <>
                <X className="size-4" /> {simple ? "Skip rest" : "Reject rest"}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Body — scrolls with a quarter of the next row peeking; toolbar above + footer
          below stay put. Horizontal overflow scrolls (the Expert table in a narrow
          card) rather than clipping under the rounded corners. */}
      <div ref={scrollRef} className="overflow-auto" style={{ maxHeight }}>
        {simple ? (
          <ul>
            {data.rows.map((row) => {
              const status = statuses[row.id] ?? "pending";
              const parts = renderSimpleRow!(row);
              return (
                <li
                  key={row.id}
                  className="flex items-center gap-2 @xl:gap-3 px-3 @xl:px-4 py-2.5 border-b border-neutral-100 last:border-0"
                >
                  {parts.tier && <div className="shrink-0 w-[4.5rem] @xl:w-24">{parts.tier}</div>}
                  <div className={cn("min-w-0 flex-1", status === "declined" && "opacity-50")}>
                    <div className={cn("font-semibold text-sm text-neutral-900 truncate", status === "declined" && "line-through")}>
                      {parts.title}
                    </div>
                    {parts.subtitle && <div className="text-xs text-neutral-500 truncate">{parts.subtitle}</div>}
                  </div>
                  {parts.metric && <div className="shrink-0">{parts.metric}</div>}
                  <RowActions row={row} status={status} disabled={locked} dispatch={dispatch} />
                </li>
              );
            })}
          </ul>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="sticky top-0 bg-white">
              <tr className="text-[10px] uppercase tracking-wide text-neutral-400">
                {data.columns.map((col) => (
                  <th key={col.key} className="text-left font-medium px-2 py-1.5 whitespace-nowrap">
                    {columnLabels?.[col.key] ?? col.label ?? col.key}
                  </th>
                ))}
                <th className="px-2 py-1.5" aria-label="review" />
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => {
                const status = statuses[row.id] ?? "pending";
                return (
                  <tr key={row.id} className="border-t border-neutral-100">
                    {data.columns.map((col, i) => (
                      <td
                        key={col.key}
                        className={cn(
                          "px-2 py-1.5 text-neutral-800 align-middle whitespace-nowrap",
                          status === "declined" && "text-neutral-400 line-through"
                        )}
                      >
                        {i === 0 ? (
                          // Keyword: dot + term. The term truncates with an ellipsis
                          // rather than wrapping, so a long keyword can't blow up the
                          // row height — it stays one line like the rest of the table.
                          <span className="flex items-center gap-2 max-w-[200px]">
                            <span className={cn("size-2 rounded-full shrink-0", DOT[status])} />
                            <span
                              className="truncate font-semibold text-neutral-900"
                              title={String((row as Record<string, unknown>)[col.key] ?? "")}
                            >
                              {renderCell(col, row)}
                            </span>
                          </span>
                        ) : (
                          renderCell(col, row)
                        )}
                      </td>
                    ))}
                    <td className="px-2 py-1.5">
                      <RowActions row={row} status={status} disabled={locked} dispatch={dispatch} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer — pinned below the scroll area; Approve stays visible */}
      <div className="px-4 py-3 border-t border-neutral-100 space-y-3">
        <div className="flex items-center gap-4 text-xs text-neutral-500 font-mono">
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-emerald-500" />
            {counts.approved} approved
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-red-500" />
            {counts.rejected} rejected
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-neutral-300" />
            {counts.open} open
          </span>
        </div>
        <Button
          className="w-full"
          disabled={locked || counts.approved === 0}
          onClick={() => onApprove(applyStatuses(artifact, statuses))}
        >
          <Zap className="size-4" />
          Approve plan · {counts.approved} {counts.approved === 1 ? "keyword" : "keywords"}
        </Button>
      </div>
    </div>
  );
}
